import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BuyerSearchResult } from "@/types/buyerSearch";
import { Building, Clock, TrendingUp, Phone, Mail, Plus } from "lucide-react";
import { useState } from "react";

interface BuyerCardProps {
  buyer: BuyerSearchResult;
  onAddToList: (buyerId: string) => void;
  onContact: (buyerId: string) => void;
  isFirstCard?: boolean;
}

export const BuyerCard = ({
  buyer,
  onAddToList,
  onContact,
  isFirstCard = false
}: BuyerCardProps) => {
  const [isSelected, setIsSelected] = useState(false);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'default';
      case 'trial':
        return 'secondary';
      case 'expired':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const displayMarkets = buyer.preferred_markets?.slice(0, 4) || [];
  const additionalMarketsCount = (buyer.preferred_markets?.length || 0) - 4;

  return (
    <Card 
      className={`transition-all duration-200 hover:shadow-md cursor-pointer ${
        isSelected ? 'ring-2 ring-primary' : ''
      }`}
      onClick={() => setIsSelected(!isSelected)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarImage src={buyer.profile_image_url || undefined} />
              <AvatarFallback>{getInitials(buyer.full_name)}</AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold">{buyer.full_name}</h3>
              {buyer.company_name && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Building className="h-3 w-3" />
                  {buyer.company_name}
                </div>
              )}
            </div>
          </div>
          <Badge 
            variant={getStatusBadgeVariant(buyer.subscription_status) as any}
            className="text-xs"
          >
            {getStatusLabel(buyer.subscription_status)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span>{buyer.deals_annually} deals/year</span>
          </div>
          {buyer.average_close_time && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-primary" />
              <span>{buyer.average_close_time}</span>
            </div>
          )}
        </div>

        {/* Purchasing Types */}
        {buyer.purchasing_type && buyer.purchasing_type.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Purchasing Types:</div>
            <div className="flex flex-wrap gap-1">
              {buyer.purchasing_type.slice(0, 3).map((type) => (
                <Badge key={type} variant="outline" className="text-xs">
                  {type}
                </Badge>
              ))}
              {buyer.purchasing_type.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{buyer.purchasing_type.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Preferred Markets */}
        {displayMarkets.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Preferred Markets:</div>
            <div className="flex flex-wrap gap-1">
              {displayMarkets.map((market) => (
                <Badge key={market} variant="secondary" className="text-xs">
                  {market}
                </Badge>
              ))}
              {additionalMarketsCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  +{additionalMarketsCount} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="pt-2">
          <Button 
            size="sm" 
            className="w-full text-xs sm:text-sm"
            onClick={(e) => {
              e.stopPropagation();
              onAddToList(buyer.id);
            }}
            {...(isFirstCard ? { 'data-tour': 'add-to-list' } : {})}
          >
            <Plus className="h-4 w-4 sm:mr-1" />
            <span className="hidden sm:inline">Add to List</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        {/* Contact Actions */}
        {buyer.has_viewed_properties ? (
          <div className="flex gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="flex-1 text-xs sm:text-sm"
              onClick={(e) => {
                e.stopPropagation();
                onContact(buyer.id);
              }}
              {...(isFirstCard ? { 'data-tour': 'contact-buyer' } : {})}
            >
              <Mail className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Contact</span>
            </Button>
            {buyer.phone_number && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="px-3"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`tel:${buyer.phone_number}`, '_self');
                }}
              >
                <Phone className="h-4 w-4" />
              </Button>
            )}
          </div>
        ) : (
          <div className="text-center py-2 text-sm text-muted-foreground bg-muted/50 rounded-md">
            Contact info available after viewing your properties
          </div>
        )}
      </CardContent>
    </Card>
  );
};